import QuestionTitle from '@/ui/components/inspectionReport/items/QuestionTitle.vue';
import { h, defineComponent, inject } from 'vue';
import { ImageAsset } from '@/ui/components/asset/ImageAsset';
import TableBox from '@/ui/components/inspectionReport/items/table/TableBox.vue';
import { ItemProps } from '@/ui/components/inspectionReport/types';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import {
  LegacyFormParamResponse,
  LegacyItem,
  LegacyItemResponseType,
} from '@/ui/components/inspectionReport/legacyTypes';
import { DocumentAsset } from '@/ui/components/asset/DocumentAsset';
import QuestionNotes from '@/ui/components/inspectionReport/items/QuestionNotes.vue';
import { isLowQualityImagesFromReportPageKey } from '@/ui/injectionKeys';
import { isPdf } from '@/ui/helpers/systemHelper';

export const Dropdown = defineComponent<ItemProps>({
  name: 'Dropdown',
  props: ['item'],
  setup(props) {
    const isLowQualityImages = inject(isLowQualityImagesFromReportPageKey, false);
    const { resource, isTranslationEnabled } = useInspectionReport();

    const getItemResponseList = (response: LegacyItemResponseType | undefined): Array<string> => {
      if (!response) {
        return [];
      }

      if (typeof response === 'string') {
        return [response];
      }

      return response as Array<string>;
    };

    const getSelectedResponses = (item: LegacyItem) => {
      if (!resource.value?.form) {
        return [];
      }

      const itemResponses = getItemResponseList(item.response);

      if (!Array.isArray(itemResponses)) {
        return [];
      }

      return resource.value.form.params.response_sets
        .find(({ uuid }) => (uuid === item.params.response_set))?.responses
        .filter(({ uuid }) => {
          return itemResponses ? itemResponses.includes(uuid) : false;
        }) ?? [];
    };

    return function render() {
      let selectedResponses: Array<LegacyFormParamResponse> = [];

      if (!resource.value) {
        return null;
      }

      if (props.item.responded === true && props.item.response) {
        selectedResponses = getSelectedResponses(props.item);
      }

      const getResponseTitle = (response: LegacyFormParamResponse, isLast: boolean) => {
        return isTranslationEnabled && response.title_translated
          ? (isLast ? response.title_translated : `${response.title_translated},`)
          : (isLast ? response.title : `${response.title},`);
      };

      return h('tbody', [
        h('tr', [
          h(TableBox, {
            props: {
              colspan: 4,
            },
          }, [
            props.item.image_id
              ? h(ImageAsset, {
                class: 'mb-2 inspection-report__question-img',
                props: {
                  photo: props.item,
                  isLowQualityImages: isPdf && isLowQualityImages,
                },
              })
              : null,
            h('span', { class: 'font-semibold' }, [h(QuestionTitle, { props: { question: props.item } })]),
            h(DocumentAsset, { props: { item: props.item } }),
          ]),
          h(TableBox, { props: { colspan: 4 } }, [
            selectedResponses.length === 0
              ? h('span', ['-'])
              : h('div', { class: 'flex flex-row items-center flex-wrap' }, [
                selectedResponses.map((response, index) => h('div', { class: 'flex flex-row items-center mr-2' }, [
                  h('span', [getResponseTitle(response, index + 1 === selectedResponses.length)]),
                ])),
              ]),
          ]),
        ]),
        props.item.notes
        && h('tr', [
          h(TableBox, {
            class: 'pl-2',
            props: {
              colspan: 8,
            },
          }, [
            h(QuestionNotes, { props: { notes: props.item.notes } }),
          ]),
        ]),
        props.item.photos && props.item.photos.length > 0
        && h('tr', [
          h(TableBox, {
            class: 'pl-2',
            props: {
              colspan: 8,
            },
          }, [
            h('div', { class: 'pl-2 flex flex-wrap' }, [
              props.item.photos.map(photo => [
                h('span', `${photo.index}. `),
                h(ImageAsset, {
                  class: 'mr-3 inline-block inspection-report__image-drawing-thumbnail',
                  props: {
                    photo,
                    isLowQualityImages: isPdf && isLowQualityImages,
                  },
                }),
              ]),
            ]),
          ]),
        ]),
      ]);
    };
  },
});
